import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import { asyncWithLDProvider, basicLogger } from 'launchdarkly-react-client-sdk';
import App from './App';
import { store } from './store/store';
import './styles/main.scss';
import { initializeBootstrapUtilities } from './utils/bootstrap.utils';
import { requestService, streamService, translationService } from './servicesInitializer';
import { ISharedServicesContext, SharedServicesContext } from '@shared/hooks/sharedServices.context';
import { config } from './config/config';
import { Environments } from '@shared/constants/environments';

const queryClient = new QueryClient();

initializeBootstrapUtilities();

const rootElement: HTMLElement | null = document.getElementById('root');
if (!rootElement) {
	throw new Error('Failed to find the root element');
}

const root = createRoot(rootElement);

const sharedServiceContext: ISharedServicesContext = {
	requestService,
	translationService,
};

window.onbeforeunload = function () {
	streamService.closeAllStreams();
};

(async () => {
	const LDProvider = await asyncWithLDProvider({
		clientSideID: config.launchDarklyClientSideId,
		reactOptions: {
			useCamelCaseFlagKeys: false, // Preserve original flag key format
		},
		options: {
			logger: basicLogger({
				level: import.meta.env.REACT_APP_STAGE === 'prod' ? 'none' : 'warn',
			}),
		},
		context: {
			kind: 'multi',
			user: {
				key: 'anonymous',
				anonymous: true,
			},
			project: {
				key: 'unknown-project',
			},
			role: {
				key: 'unknown-role',
			},
			environment: {
				key: import.meta.env.REACT_APP_STAGE || 'unknown',
				name: import.meta.env.REACT_APP_STAGE || 'unknown',
			},
		},
	});

	root.render(
		<Provider store={store}>
			<SharedServicesContext.Provider value={sharedServiceContext}>
				<QueryClientProvider client={queryClient}>
					<Router>
						<PostHogProvider client={posthog}>
							<LDProvider>
								<App />
							</LDProvider>
						</PostHogProvider>
					</Router>
				</QueryClientProvider>
			</SharedServicesContext.Provider>
		</Provider>
	);
})();
